// src/admin/screens/borrowers/tabs/NotesTab.tsx
// Free-text audit notes scoped to the borrower (optionally loan-scoped).
// Two round-trips: fetch notes, then bulk-fetch the author admin rows.
// This is simpler than a PostgREST embedded select because the
// generated types have `Relationships: []` on borrower_notes, so the
// embedded shape can't be inferred without a hand-rolled cast.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import { useAuth } from '@admin/auth/useAuth';
import { useRole } from '@admin/auth/useRole';
import Card from '@admin/components/Card';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import { formatCatDateTime } from '@admin/utils/dates';
import type { Database } from '@/lib/database.types';

type NoteRow = Database['public']['Tables']['borrower_notes']['Row'];

interface NoteWithAuthor extends NoteRow {
  author_name: string;
}

export default function NotesTab({ borrowerId }: { borrowerId: string }) {
  const qc = useQueryClient();
  const { admin } = useAuth();
  const { can } = useRole();
  const toast = useToast();
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['borrower-notes', borrowerId],
    queryFn: async (): Promise<NoteWithAuthor[]> => {
      const { data: notes, error: notesErr } = await supabase
        .from('borrower_notes')
        .select('*')
        .eq('borrower_id', borrowerId)
        .order('created_at', { ascending: false });
      if (notesErr) throw notesErr;
      const list = notes ?? [];
      if (list.length === 0) return [];

      const authorIds = Array.from(new Set(list.map((n) => n.author_admin_id)));
      const { data: admins, error: adminsErr } = await supabase
        .from('admin_users')
        .select('id, display_name')
        .in('id', authorIds);
      if (adminsErr) throw adminsErr;
      const nameById = new Map((admins ?? []).map((a) => [a.id, a.display_name]));

      return list.map((n) => ({ ...n, author_name: nameById.get(n.author_admin_id) ?? 'Unknown' }));
    },
  });

  const save = async () => {
    if (!admin || body.trim().length === 0) return;
    setBusy(true);
    try {
      const { error: insertErr } = await supabase.from('borrower_notes').insert({
        borrower_id: borrowerId,
        author_admin_id: admin.id,
        body: body.trim(),
      });
      if (insertErr) throw insertErr;
      await recordAudit({
        action: 'borrower.add_note',
        target_type: 'borrower',
        target_id: borrowerId,
      });
      setBody('');
      await qc.invalidateQueries({ queryKey: ['borrower-notes', borrowerId] });
      toast({ kind: 'success', msg: 'Note saved' });
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {can('borrowers.add_note') && (
        <Card title="Add note">
          <FormField label="Note body" htmlFor="note-body">
            <textarea
              id="note-body"
              rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </FormField>
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={save} loading={busy} disabled={body.trim().length === 0}>
              Save note
            </Button>
          </div>
        </Card>
      )}
      <Card title="Note history">
        {isLoading && <p className="small">Loading notes…</p>}
        {error && <p style={{ color: 'var(--c-danger)' }}>{(error as Error).message}</p>}
        {!isLoading && !error && (data ?? []).length === 0 && (
          <p className="small">No notes yet.</p>
        )}
        {(data ?? []).map((n) => (
          <div key={n.id} style={{ padding: '10px 0', borderTop: '1px solid var(--c-border-lt)' }}>
            <p className="small">
              {formatCatDateTime(n.created_at)} · {n.author_name}
              {n.loan_id && ' · loan-scoped'}
            </p>
            <p style={{ whiteSpace: 'pre-wrap' }}>{n.body}</p>
          </div>
        ))}
      </Card>
    </div>
  );
}
